/**
 * Owner: Masa Bayu
 * File: library-offline-sync.ts
 * Date:Time: 2026-09-15 23:21
 * Description: T4 offline availability + sync/recovery contracts (≠ T8; E3 extends).
 */
import { isEntitlementActiveForRead } from './entitlement-boundary';
import { assertT4E1SecurityIntact } from './library-security';
import { assertTimestamp, assertValidId } from './guards';
export const QXREAD_LIBRARY_SYNC_STATES = [
    'idle',
    'syncing',
    'synced',
    'conflict',
    'recovery_required',
];
export function evaluateLibraryOfflineAvailability(input) {
    assertT4E1SecurityIntact();
    if (input.entitlement.entitlementId !== input.item.entitlementId) {
        return Object.freeze({
            allowed: false,
            reason: 'Entitlement must match library item',
            nextItemState: null,
        });
    }
    if (!isEntitlementActiveForRead(input.entitlement)) {
        return Object.freeze({
            allowed: false,
            reason: 'Active entitlement required for offline availability',
            nextItemState: 'blocked',
        });
    }
    if (input.item.state === 'blocked' ||
        input.item.state === 'removed' ||
        input.item.state === 'materializing') {
        return Object.freeze({
            allowed: false,
            reason: `Library state "${input.item.state}" cannot prepare offline`,
            nextItemState: null,
        });
    }
    return Object.freeze({
        allowed: true,
        reason: 'Entitlement active — offline prepare permitted (package execution is T8)',
        nextItemState: 'offline_preparing',
    });
}
export function markLibraryItemOfflineReady(item) {
    if (item.state !== 'offline_preparing' &&
        item.state !== 'available' &&
        item.state !== 'reading') {
        throw new Error(`QXRead offline ready blocked from state "${item.state}"`);
    }
    return Object.freeze({
        ...item,
        state: 'offline_ready',
        offlineAvailable: true,
        updatedAt: new Date().toISOString(),
    });
}
export function createLibrarySyncContract(options) {
    assertValidId(options.accountId, 'QXRead library sync accountId');
    const state = options.state ?? 'idle';
    if (!QXREAD_LIBRARY_SYNC_STATES.includes(state)) {
        throw new Error(`QXRead library sync state invalid: ${state}`);
    }
    let lastSyncedAt = null;
    if (options.lastSyncedAt !== undefined && options.lastSyncedAt !== null) {
        assertTimestamp(options.lastSyncedAt, 'QXRead library lastSyncedAt');
        lastSyncedAt = options.lastSyncedAt;
    }
    return Object.freeze({
        accountId: options.accountId,
        state,
        lastSyncedAt,
        recoveryHint: options.recoveryHint ?? null,
    });
}
export function beginLibrarySync(contract) {
    return Object.freeze({
        ...contract,
        state: 'syncing',
        recoveryHint: null,
    });
}
export function completeLibrarySync(contract, syncedAt) {
    const lastSyncedAt = syncedAt ?? new Date().toISOString();
    assertTimestamp(lastSyncedAt, 'QXRead library lastSyncedAt');
    return Object.freeze({
        ...contract,
        state: 'synced',
        lastSyncedAt,
        recoveryHint: null,
    });
}
export function markLibrarySyncConflict(contract, hint) {
    if (typeof hint !== 'string' || hint.trim().length === 0) {
        throw new Error('QXRead library conflict hint is required');
    }
    return Object.freeze({
        ...contract,
        state: 'conflict',
        recoveryHint: hint.trim(),
    });
}
export function markLibrarySyncRecoveryRequired(contract, hint) {
    if (typeof hint !== 'string' || hint.trim().length === 0) {
        throw new Error('QXRead library recovery hint is required');
    }
    return Object.freeze({
        ...contract,
        state: 'recovery_required',
        recoveryHint: hint.trim(),
    });
}
/** Mark local offline package reference stale after remote continuity/version drift. */
export function markLibraryItemOfflineStale(item) {
    if (!item.offlineAvailable && item.state !== 'offline_ready' && item.state !== 'offline_stale') {
        throw new Error(`QXRead offline stale blocked from state "${item.state}"`);
    }
    return Object.freeze({
        ...item,
        state: 'offline_stale',
        offlineAvailable: false,
        updatedAt: new Date().toISOString(),
    });
}
/** Offline library flags never grant entitlement or Reader access. */
export function offlineLibraryStateCannotGrantAccess(_item) {
    return true;
}
//# sourceMappingURL=library-offline-sync.js.map